import { useEffect, useMemo, useState } from 'react';

import { cn } from '~/utils/cn';
import {
  buildReportPreviewUrl,
  downloadAndSavePdf,
  getReportPreviewTokenRequest,
  type ReportDetail,
  type ReportFilters,
  type ReportMode,
  type ReportPeriod,
  type ReportStatusFilter,
} from '~/services/http/requests/reports';
import type { Contract } from '~/store/contracts/interfaces';

import Backdrop from '../Backdrop/view';
import Button from '../../atoms/Button/view';
import FilterChips from '../FilterChips/view';
import { Icon } from '../../atoms/Icon/view';
import Select from '../../atoms/Select/view';
import Stepper from '../Stepper/view';
import { Text } from '../../atoms/Text/view';

export interface GenerateReportSheetProps {
  open: boolean;
  onClose: () => void;
  contracts: Contract[];
  initialMode?: ReportMode;
  initialContractId?: string | null;
}

type Step = 0 | 1 | 2;

const PERIOD_ITEMS = [
  { key: 'all' as ReportPeriod, label: 'Todo o histórico' },
  { key: 'last_year' as ReportPeriod, label: 'Último ano' },
  { key: 'last_6_months' as ReportPeriod, label: 'Últimos 6 meses' },
];

const STATUS_ITEMS = [
  { key: 'all' as ReportStatusFilter, label: 'Todos' },
  { key: 'active' as ReportStatusFilter, label: 'Ativos' },
  { key: 'completed' as ReportStatusFilter, label: 'Quitados' },
];

const DETAIL_ITEMS = [
  { key: 'full' as ReportDetail, label: 'Completo' },
  { key: 'summary' as ReportDetail, label: 'Resumido' },
];

const STEP_LABELS = ['Modo', 'Filtros', 'Pré-visualização'];

export default function GenerateReportSheet({
  open,
  onClose,
  contracts,
  initialMode = 'consolidated',
  initialContractId = null,
}: GenerateReportSheetProps) {
  const [step, setStep] = useState<Step>(0);
  const [mode, setMode] = useState<ReportMode>(initialMode);
  const [contractId, setContractId] = useState<string | null>(initialContractId);
  const [period, setPeriod] = useState<ReportPeriod>('all');
  const [statusFilter, setStatusFilter] = useState<ReportStatusFilter>('all');
  const [detail, setDetail] = useState<ReportDetail>('full');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setMode(initialMode);
    setContractId(initialContractId);
    setPeriod('all');
    setStatusFilter('all');
    setDetail('full');
    setPreviewUrl(null);
    setError(null);
  }, [open, initialMode, initialContractId]);

  const filters = useMemo<ReportFilters>(
    () => ({ period, status: statusFilter, detail }),
    [period, statusFilter, detail],
  );

  const canAdvanceModeStep =
    mode === 'consolidated' ? true : Boolean(contractId);

  const goToPreview = async () => {
    setLoadingPreview(true);
    setError(null);
    try {
      const { token } = await getReportPreviewTokenRequest();
      const url = buildReportPreviewUrl(mode, contractId, filters, token);
      setPreviewUrl(url);
      setStep(2);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi possível gerar o preview';
      setError(message);
    } finally {
      setLoadingPreview(false);
    }
  };

  const onDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      await downloadAndSavePdf(mode, contractId, filters);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Falha ao baixar o PDF';
      setError(message);
    } finally {
      setDownloading(false);
    }
  };

  const onShare = async () => {
    if (!previewUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Relatório financeiro Paga Agro',
          text: 'Relatório gerado pela Paga Agro',
          url: previewUrl,
        });
      } catch {
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(previewUrl);
        setError(null);
      } catch {
        setError('Não foi possível copiar o link.');
      }
    }
  };

  return (
    <Backdrop open={open} onClose={onClose} position="bottom" className="max-w-[920px] h-[92vh] flex flex-col">
      <header className="flex flex-col gap-smaller px-medium pt-medium pb-small bg-primary-01 rounded-t-large">
        <div className="flex items-center justify-between">
          <Text className="text-paragraphLG color-intense-01">Gerar relatório</Text>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="p-quark rounded-full hover:bg-intense-01/10"
          >
            <Icon name="icon-close" size={18} color="#FFFFFF" rounded={false} />
          </button>
        </div>
        <Stepper total={3} current={step} labels={STEP_LABELS} />
      </header>

      <div className="flex-1 overflow-y-auto px-medium py-medium">
        {step === 0 ? (
          <StepMode
            mode={mode}
            onModeChange={setMode}
            contracts={contracts}
            contractId={contractId}
            onContractChange={setContractId}
          />
        ) : null}

        {step === 1 ? (
          <StepFilters
            mode={mode}
            period={period}
            onPeriodChange={setPeriod}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            detail={detail}
            onDetailChange={setDetail}
          />
        ) : null}

        {step === 2 ? (
          <StepPreview previewUrl={previewUrl} loading={loadingPreview} />
        ) : null}
      </div>

      {error ? (
        <div className="px-medium pb-quark">
          <Text className="text-captionLG color-warning-06">{error}</Text>
        </div>
      ) : null}

      <footer className="flex items-center justify-between gap-smaller px-medium py-small border-t border-intense-03 bg-intense-01">
        <Button
          variant="ghost"
          text={step === 0 ? 'Cancelar' : 'Voltar'}
          onClick={() => {
            if (step === 0) onClose();
            else setStep((step - 1) as Step);
          }}
          className={cn(step === 0 ? 'text-intense-06' : '')}
        />

        {step === 0 ? (
          <Button
            text="Continuar"
            onClick={() => setStep(1)}
            disabled={!canAdvanceModeStep}
            variant="primary"
            className="h-[48px] px-medium"
          />
        ) : null}

        {step === 1 ? (
          <Button
            text="Gerar pré-visualização"
            onClick={goToPreview}
            loading={loadingPreview}
            variant="primary"
            className="h-[48px] px-medium"
          />
        ) : null}

        {step === 2 ? (
          <div className="flex items-center gap-smaller">
            <Button
              variant="outline"
              text="Compartilhar"
              onClick={onShare}
              leftIcon="icon-share"
              className="h-[48px] px-small"
            />
            <Button
              variant="primary"
              text="Baixar PDF"
              onClick={onDownload}
              loading={downloading}
              leftIcon="icon-download"
              iconColor="#FFFFFF"
              className="h-[48px] px-medium"
            />
          </div>
        ) : null}
      </footer>
    </Backdrop>
  );
}

function StepMode({
  mode,
  onModeChange,
  contracts,
  contractId,
  onContractChange,
}: {
  mode: ReportMode;
  onModeChange: (m: ReportMode) => void;
  contracts: Contract[];
  contractId: string | null;
  onContractChange: (id: string | null) => void;
}) {
  return (
    <div className="flex flex-col gap-medium">
      <Text className="text-bodyLG color-intense-07">
        Escolha o tipo de relatório que deseja gerar.
      </Text>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-smaller">
        <ModeCard
          active={mode === 'consolidated'}
          onClick={() => onModeChange('consolidated')}
          icon="icon-grid"
          title="Relatório consolidado"
          description="Visão geral de todos os seus contratos: indicadores, evolução e situação financeira."
        />
        <ModeCard
          active={mode === 'contract'}
          onClick={() => onModeChange('contract')}
          icon="icon-document"
          title="Por contrato"
          description="Detalhamento de um contrato específico com timeline, parcelas e histórico."
        />
      </div>

      {mode === 'contract' ? (
        <div className="rounded-medium bg-intense-02 border border-intense-03 p-small">
          <Select
            label="Contrato"
            placeholder="Selecione um contrato"
            value={contractId ?? undefined}
            onChange={(v) => onContractChange(v as string)}
            options={contracts.map((c) => ({
              value: c.id,
              label: `${c.institution.name} · #${c.contractNumber}`,
              description: c.cropDescription ?? undefined,
            }))}
          />
        </div>
      ) : null}
    </div>
  );
}

function ModeCard({
  active,
  onClick,
  icon,
  title,
  description,
}: {
  active: boolean;
  onClick: () => void;
  icon: 'icon-grid' | 'icon-document';
  title: string;
  description: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-left rounded-medium border p-small transition-all flex flex-col gap-quark',
        active
          ? 'border-primary-01 bg-support-01 shadow-level1'
          : 'border-intense-03 bg-intense-01 hover:border-intense-04',
      )}
    >
      <div
        className={cn(
          'w-[40px] h-[40px] rounded-medium flex items-center justify-center',
          active ? 'bg-primary-01' : 'bg-intense-02',
        )}
      >
        <Icon name={icon} size={20} color={active ? '#FFFFFF' : '#1D4733'} rounded={false} />
      </div>
      <Text className="text-paragraphLG color-complement-01">{title}</Text>
      <Text className="text-bodyMD color-intense-06">{description}</Text>
    </button>
  );
}

function StepFilters({
  mode,
  period,
  onPeriodChange,
  statusFilter,
  onStatusChange,
  detail,
  onDetailChange,
}: {
  mode: ReportMode;
  period: ReportPeriod;
  onPeriodChange: (p: ReportPeriod) => void;
  statusFilter: ReportStatusFilter;
  onStatusChange: (s: ReportStatusFilter) => void;
  detail: ReportDetail;
  onDetailChange: (d: ReportDetail) => void;
}) {
  return (
    <div className="flex flex-col gap-medium">
      <Text className="text-bodyLG color-intense-07">
        Ajuste o escopo do relatório. Você pode deixar nos padrões.
      </Text>

      {mode === 'consolidated' ? (
        <Section title="Período" description="Janela usada para cálculos de pago e evolução.">
          <FilterChips items={PERIOD_ITEMS} active={period} onChange={onPeriodChange} />
        </Section>
      ) : null}

      {mode === 'consolidated' ? (
        <Section title="Status dos contratos">
          <FilterChips items={STATUS_ITEMS} active={statusFilter} onChange={onStatusChange} />
        </Section>
      ) : null}

      <Section
        title="Detalhamento"
        description={
          detail === 'full'
            ? 'Completo inclui o cronograma integral de parcelas.'
            : 'Resumido inclui apenas as próximas 12 parcelas e as últimas pagas.'
        }
      >
        <FilterChips items={DETAIL_ITEMS} active={detail} onChange={onDetailChange} />
      </Section>
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-quark">
      <Text className="text-paragraphMD color-complement-01">{title}</Text>
      {description ? (
        <Text className="text-captionLG color-intense-06">{description}</Text>
      ) : null}
      <div className="mt-quark">{children}</div>
    </div>
  );
}

function StepPreview({ previewUrl, loading }: { previewUrl: string | null; loading: boolean }) {
  if (loading || !previewUrl) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-smaller">
          <Icon name="icon-loader" size={32} color="#1D4733" className="animate-spin" rounded={false} />
          <Text className="text-bodyMD color-intense-06">Renderizando documento…</Text>
        </div>
      </div>
    );
  }
  return (
    <div
      className="w-full rounded-medium border border-intense-03 overflow-hidden bg-intense-02"
      style={{ height: '70vh', minHeight: 480 }}
    >
      <iframe
        src={previewUrl}
        title="Pré-visualização do relatório"
        className="w-full h-full block border-0"
      />
    </div>
  );
}
